/**
 * The DOM and the tape.
 *
 * ## Why the refusal is a first-class state here
 *
 * Level 2 is a separate exchange subscription from Level 1, and several times
 * the price — $41.00/month against $9.00 across the CME bundle. A trader with
 * perfectly good real-time quotes may hold no right to the book behind them,
 * and this panel's most common state on a real deployment is *refused*.
 *
 * So the refusal is not an error toast. It says what depth is, who sells it,
 * and — importantly — that it is not an AEGIS upgrade. A trader sent to our
 * billing page for something only CME sells is annoyed twice.
 *
 * ## Why the ladder is drawn from the data's own numbers
 *
 * Bar widths come from the largest resting size in the visible book, not from a
 * constant. A fixed scale makes a quiet market look identical to a busy one,
 * which is the one comparison a DOM is for.
 *
 * ## What is deliberately not here
 *
 * No click-to-trade, no order entry, no working-order column. AEGIS never
 * executes (`CLAUDE.md`), and a DOM is the widget where that boundary is most
 * tempting to blur. The ladder is read-only and has nowhere to put an order.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { JSX } from 'react';
import type { DepthResponse } from '@aegis/api-client';
import { ApiError } from '@aegis/api-client';
import { api } from '../lib/api.js';
import { Tape } from './Tape.js';

const REFRESH_MS = 1500;

interface Refusal {
  readonly message: string;
  readonly fixable: boolean;
}

export function OrderBook({
  symbol, precision, live,
}: {
  symbol: string;
  precision: number;
  /** False during replay. A live book beside a chart from three weeks ago is
   *  the exact confusion the replay badge exists to prevent. */
  live: boolean;
}): JSX.Element {
  const [book, setBook] = useState<DepthResponse | null>(null);
  const [refusal, setRefusal] = useState<Refusal | null>(null);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const poll = useCallback(async () => {
    try {
      setBook(await api.depth(symbol, 10));
      setRefusal(null);
    } catch (e) {
      if (e instanceof ApiError && e.status === 403) {
        setBook(null);
        setRefusal({
          message: e.message,
          // A device problem the trader can fix in settings, versus a
          // subscription only the exchange sells.
          fixable: e.code !== 'no_depth_subscription',
        });
        // Stop polling a refusal. Retrying a licensing decision every 1.5
        // seconds achieves nothing and fills the log.
        if (timer.current) { clearInterval(timer.current); timer.current = null; }
        return;
      }
      // A transient failure is left alone — the next poll will fix it, and a
      // banner that flickers on every dropped request is worse than none.
    }
  }, [symbol]);

  useEffect(() => {
    setRefusal(null);
    /* No symbol, no request. `GET /v1/depth/` is a different route, and its
       404 reads in the log exactly like a symbol the feed does not carry —
       which is a real condition worth noticing, so it must not be drowned. */
    if (!live || !symbol) {
      setBook(null);
      return undefined;
    }
    void poll();
    timer.current = setInterval(() => { void poll(); }, REFRESH_MS);
    return () => {
      if (timer.current) clearInterval(timer.current);
      timer.current = null;
    };
  }, [poll, live, symbol]);

  if (!symbol) {
    return (
      <aside className="panel dom">
        <div className="panel-head"><h3 className="panel-title">Book</h3></div>
        <p className="panel-empty">Pick an instrument to see its book.</p>
      </aside>
    );
  }

  if (!live) {
    return (
      <aside className="panel dom">
        <div className="panel-head"><h3 className="panel-title">Book</h3></div>
        <p className="panel-empty">
          Hidden during replay. The book is live and the chart is not — showing
          them together is the confusion replay exists to prevent.
        </p>
      </aside>
    );
  }

  // A depth refusal must not take the tape with it. Prints are Level 1 and the
  // book is Level 2 — the single most likely state on a real deployment is a
  // trader entitled to one and not the other, and blanking both would refuse
  // them data they are paying for.
  if (refusal) {
    return (
      <aside className="panel dom">
        <div className="panel-head"><h3 className="panel-title">Book</h3></div>
        <div className="dom-refusal">
          <p>{refusal.message}</p>
          {!refusal.fixable && (
            <p className="dim">
              Level 2 is sold by the exchange, not by AEGIS. Nothing on your AEGIS
              plan turns it on, and nothing you can buy here will.
            </p>
          )}
        </div>
        <Tape symbol={symbol} precision={precision} live={live} />
      </aside>
    );
  }

  if (!book) {
    return (
      <aside className="panel dom">
        <div className="panel-head"><h3 className="panel-title">Book</h3></div>
        <p className="panel-empty">Loading…</p>
        <Tape symbol={symbol} precision={precision} live={live} />
      </aside>
    );
  }

  // Scaled to the visible book, not a constant — a fixed scale makes a quiet
  // market look identical to a busy one.
  const peak = Math.max(
    1, ...book.bids.map((l) => l.size), ...book.asks.map((l) => l.size),
  );
  const px = (n: number): string => n.toFixed(precision);
  const imb = book.imbalance;

  return (
    <aside className="panel dom">
      <div className="panel-head">
        <h3 className="panel-title">Book</h3>
        {book.source === 'mock' && <span className="badge badge-warn">GENERATED</span>}
        {book.delayedByMs > 0 && (
          <span className="badge badge-warn">
            {Math.round(book.delayedByMs / 60_000)}-MIN DELAYED
          </span>
        )}
      </div>

      {/* Asks descend to the inside so the ladder reads top-to-bottom like a
          real DOM: offers above, bids below, spread in the middle. */}
      <ol className="ladder">
        {[...book.asks].reverse().map((l) => (
          <li key={`a${l.price}`} className="rung ask">
            <span className="rung-bar" style={{ width: `${(l.size / peak) * 100}%` }} />
            <span className="rung-price mono">{px(l.price)}</span>
            <span className="rung-size mono">{l.size}</span>
          </li>
        ))}
        <li className="rung spread">
          <span className="dim">
            spread {book.spread === null ? '—' : px(book.spread)}
          </span>
          <span className={`dim ${imb === null ? '' : imb > 0 ? 'pos' : 'neg'}`}>
            {imb === null ? 'no book' : `${(imb * 100).toFixed(0)}% ${imb > 0 ? 'bid' : 'offered'}`}
          </span>
        </li>
        {book.bids.map((l) => (
          <li key={`b${l.price}`} className="rung bid">
            <span className="rung-bar" style={{ width: `${(l.size / peak) * 100}%` }} />
            <span className="rung-price mono">{px(l.price)}</span>
            <span className="rung-size mono">{l.size}</span>
          </li>
        ))}
      </ol>

      <Tape symbol={symbol} precision={precision} live={live} />

    </aside>
  );
}
